export default class extends HTMLElement {
  connectedCallback () {
    this.interval = parseInt(this.dataset.interval, 10) || 1000
    // This element is shared by the templates ZIP import and the envelopes bulk send -- the
    // latter processes envelopes, not templates, and sends them rather than importing them.
    // Every string below is built from these two instead of the hardcoded "templates"/"imported"
    // wording so a bulk send stops describing itself as an import. Defaults reproduce the
    // original literal text exactly when neither is set.
    this.itemNoun = this.dataset.itemNoun || 'templates'
    this.itemVerb = this.dataset.itemVerb || 'imported'

    this.poll()
  }

  capitalize (word) {
    return word.charAt(0).toUpperCase() + word.slice(1)
  }

  // Strips a regular "-ed" past-tense ending to recover an action label ("imported" -> "Import").
  // "sent" has no such ending, so it passes through unchanged ("Sent") -- not perfect grammar,
  // but correct in substance: it names the action that failed instead of calling a send an import.
  actionLabel () {
    return this.capitalize(this.itemVerb.replace(/ed$/, ''))
  }

  disconnectedCallback () {
    clearTimeout(this.timer)
  }

  target (name) {
    return this.querySelector(`[data-target="${name}"]`)
  }

  async poll () {
    try {
      const resp = await fetch(this.dataset.url, { headers: { Accept: 'application/json' } })

      if (resp.ok) {
        const done = this.render(await resp.json())

        if (done) return
      }
    } catch {
      // A dropped poll is not fatal — the job keeps running, so just try again next tick.
    }

    this.timer = setTimeout(() => this.poll(), this.interval)
  }

  render (data) {
    const total = data.total || 0

    // total is 0 between the redirect and the worker opening the zip — show that it is starting
    // rather than a misleading "0 / 0".
    this.target('counter').textContent = total ? `${data.processed} / ${total}` : 'Preparing…'
    this.target('imported').textContent = `${data.imported} ${this.itemVerb}`
    this.target('bar').value = data.processed
    this.target('bar').max = Math.max(total, 1)
    this.target('current').textContent = data.current || ''

    if (data.failures?.length) {
      const list = this.target('failures')

      list.replaceChildren(...data.failures.map((f) => {
        const li = document.createElement('li')

        li.textContent = `${f.name} — ${f.error}`

        return li
      }))

      this.target('failures-panel').classList.remove('hidden')
    }

    if (data.status !== 'done' && data.status !== 'error') return false

    this.target('current').textContent = ''

    // A clean run has nothing left to say — reloading dismisses the modal and shows the new
    // templates in the list behind it, which is the real confirmation.
    if (data.status === 'done' && !data.failures?.length) {
      this.finish()

      return true
    }

    const summary = this.target('summary')

    summary.textContent = data.status === 'error'
      ? `${this.actionLabel()} failed: ${data.error}`
      : `${this.capitalize(this.itemVerb)} ${data.imported} of ${total} ${this.itemNoun}.`
    summary.classList.remove('hidden')

    // Something was skipped, so keep the modal up until it has been read.
    this.target('done-panel').classList.remove('hidden')
    this.target('done-button').addEventListener('click', () => this.finish(), { once: true })

    return true
  }

  finish () {
    // Navigate rather than close: the list behind was rendered before any of these templates
    // existed. Going to the list URL reloads it when the modal is open over it, and is also the
    // right destination when this page was opened standalone — where reload() would just loop.
    window.location.href = this.dataset.returnUrl || '/templates'
  }
}
